import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

const TIER_DISCOUNT_ATTRIBUTE = 'data-tier-discount-pct';
const ANALYTICS_CUSTOM_EVENT = 'ab:theme-analytics';

function publishAnalyticsEvent(eventName, data = {}) {
  document.dispatchEvent(
    new CustomEvent(ANALYTICS_CUSTOM_EVENT, {
      detail: {
        eventName,
        data,
      },
    })
  );
}

class ABTieredBundlePicker extends HTMLElement {
  stickyIntersectionObserver = null;

  stickyExitObserver = null;

  previewBarObserver = null;

  stickyExitTargets = new Set();

  stickyTriggerPassed = false;

  stickyHiddenByExitTarget = false;

  stickyFallbackRaf = null;

  connectedCallback() {
    this.tierInputs.forEach((input) => input.addEventListener('change', this.handleTierChange));
    this.variantSelects.forEach((select) => select.addEventListener('change', this.handleVariantChange));
    this.button?.addEventListener('click', this.handleAdd);
    this.stickyButton?.addEventListener('click', this.handleStickyAdd);
    this.stickyEdit?.addEventListener('click', this.handleStickyEdit);

    this.applyPurchaseControlVisibility();
    this.formatVariantOptionLabels();
    this.initializeReferenceVariant();
    this.syncInactiveTiersToReference();
    this.updateTierState();
    this.updateVariantThumbnails();
    this.updatePrices();
    this.setupStickyObserver();
    this.setupStickyExitObserver();
    this.setupStickyFallback();
    this.setupPreviewBarOffset();
  }

  disconnectedCallback() {
    this.tierInputs.forEach((input) => input.removeEventListener('change', this.handleTierChange));
    this.variantSelects.forEach((select) => select.removeEventListener('change', this.handleVariantChange));
    this.button?.removeEventListener('click', this.handleAdd);
    this.stickyButton?.removeEventListener('click', this.handleStickyAdd);
    this.stickyEdit?.removeEventListener('click', this.handleStickyEdit);
    this.stickyIntersectionObserver?.disconnect();
    this.stickyExitObserver?.disconnect();
    this.previewBarObserver?.disconnect();
    window.removeEventListener('scroll', this.handleStickyFallbackRecalculate);
    window.removeEventListener('resize', this.handleStickyFallbackRecalculate);
    if (this.stickyFallbackRaf) {
      cancelAnimationFrame(this.stickyFallbackRaf);
      this.stickyFallbackRaf = null;
    }
    this.stickyExitTargets.clear();

    this.restorePurchaseControlVisibility();
  }

  get tierInputs() {
    return /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('.ab-tiered-bundle-picker__tier-summary input')
    );
  }

  get activeTierInput() {
    return /** @type {HTMLInputElement | null} */ (
      this.querySelector('.ab-tiered-bundle-picker__tier-summary input:checked')
    );
  }

  get activeTierCount() {
    return Number(this.activeTierInput?.value || 1);
  }

  get variantRows() {
    return /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.ab-tiered-bundle-picker__variant-row'));
  }

  get variantSelects() {
    return /** @type {NodeListOf<HTMLSelectElement>} */ (
      this.querySelectorAll('.ab-tiered-bundle-picker__variant-select')
    );
  }

  get tierCards() {
    return /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-tier-card]'));
  }

  get activeTierCard() {
    return /** @type {HTMLElement | null} */ (this.activeTierInput?.closest('[data-tier-card]') || null);
  }

  get activeVariantSelects() {
    return /** @type {HTMLSelectElement[]} */ (
      Array.from(this.activeTierCard?.querySelectorAll('.ab-tiered-bundle-picker__variant-select') || [])
    );
  }

  get button() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('.ab-tiered-bundle-picker__button'));
  }

  get errorElement() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.ab-tiered-bundle-picker__error'));
  }

  get stickyBar() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.ab-tiered-bundle-picker__sticky'));
  }

  get stickySummary() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-sticky-summary]'));
  }

  get stickyVariants() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-sticky-variants]'));
  }

  get stickyButton() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-sticky-button]'));
  }

  get stickyEdit() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-sticky-edit]'));
  }

  get moneyFormat() {
    return this.dataset.moneyFormat || '${{amount}}';
  }

  get currency() {
    return this.dataset.currency || 'USD';
  }

  get showCompareAtPrice() {
    return this.dataset.showCompareAtPrice === 'true';
  }

  handleTierChange = () => {
    this.syncActiveTierToReference();
    this.syncInactiveTiersToReference();
    this.updateTierState();
    this.updateVariantThumbnails();
    this.updatePrices();
    this.updateProductPresentationFromReference();
    this.clearError();
    publishAnalyticsEvent('ab_bundle_tier_selected', this.getActiveTierAnalyticsPayload());
  };

  handleVariantChange = (event) => {
    if (event.target instanceof HTMLSelectElement) {
      this.referenceVariantId = event.target.value;
      this.updateVariantThumbnail(event.target);
      this.syncInactiveTiersToReference();
      this.updateVariantThumbnails();
      this.updateProductPresentationFromReference();
    }

    this.updatePrices();
    this.clearError();
  };

  handleStickyAdd = () => {
    publishAnalyticsEvent('ab_sticky_add_clicked', {
      ...this.getActiveTierAnalyticsPayload(),
      surface: 'tiered_bundle_picker_sticky',
    });
    this.handleAdd();
  };

  handleStickyEdit = () => {
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });

    window.setTimeout(() => {
      this.activeTierInput?.focus({ preventScroll: true });
    }, 350);
  };

  handleAdd = async () => {
    const selectedVariantIds = this.getSelectedVariantIds();

    if (!selectedVariantIds.length) {
      this.showError('Choose an available style before adding to cart.');
      return;
    }

    this.setButtonState('adding');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: this.buildCartItems(selectedVariantIds),
          sections: this.getCartSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });

      const payload = await response.json();

      if (!response.ok || payload.status) {
        this.handleCartError(payload);
        return;
      }

      this.clearError();
      this.animateAddToCart();
      this.animateFlyToCart();
      document.dispatchEvent(
        new CartAddEvent(payload, this.id || 'ab-tiered-bundle-picker', {
          source: 'ab-tiered-bundle-picker',
          itemCount: selectedVariantIds.length,
          variantId: selectedVariantIds[0]?.toString(),
          sections: payload.sections,
        })
      );
      publishAnalyticsEvent('ab_bundle_offer_added', {
        ...this.getActiveTierAnalyticsPayload(),
        ab_source: 'ab-tiered-bundle-picker',
        item_count: selectedVariantIds.length,
      });
      this.setButtonState('default');
    } catch (error) {
      console.error(error);
      this.handleCartError({
        message: 'We could not add this offer. Please try again.',
      });
    }
  };

  updateTierState() {
    const activeCount = this.activeTierCount;

    this.tierInputs.forEach((input) => {
      const tierCard = input.closest('[data-tier-card]');
      const tierBody = tierCard?.querySelector('[data-tier-body]');
      tierCard?.classList.toggle('is-selected', input.checked);
      if (tierBody instanceof HTMLElement) {
        tierBody.hidden = !input.checked;
      }
    });

    this.updateButtonLabel();
  }

  updateVariantThumbnails() {
    this.variantSelects.forEach((select) => this.updateVariantThumbnail(select));
  }

  updateVariantThumbnail(select) {
    if (!(select instanceof HTMLSelectElement)) return;

    const row = select.closest('.ab-tiered-bundle-picker__variant-row');
    const image = row?.querySelector('.ab-tiered-bundle-picker__variant-thumb img');
    const imageSrc = select.selectedOptions[0]?.dataset.imageSrc;

    if (image instanceof HTMLImageElement && imageSrc) {
      image.src = imageSrc;
    }
  }

  formatVariantOptionLabels() {
    this.querySelectorAll('.ab-tiered-bundle-picker__variant-select option').forEach((option) => {
      if (!(option instanceof HTMLOptionElement)) return;

      option.dataset.originalLabel ||= option.textContent?.trim() || '';
      option.textContent = this.formatVariantOptionLabel(option.dataset.originalLabel);
    });
  }

  formatVariantOptionLabel(label) {
    return label.replace(/\b(60|80|110|150)cm\b/g, (_match, size) => {
      const inches = {
        60: '23.6',
        80: '31.5',
        110: '43.3',
        150: '59.1',
      }[size];

      return inches ? `${inches} in (${size} cm)` : `${size} cm`;
    });
  }

  initializeReferenceVariant() {
    this.referenceVariantId ||= this.activeVariantSelects[0]?.value || this.variantSelects[0]?.value || '';
  }

  get referenceVariantId() {
    return this.dataset.referenceVariantId || '';
  }

  set referenceVariantId(variantId) {
    if (variantId) {
      this.dataset.referenceVariantId = variantId;
    } else {
      delete this.dataset.referenceVariantId;
    }
  }

  syncActiveTierToReference() {
    this.syncSelectsToReference(this.activeVariantSelects);
  }

  syncInactiveTiersToReference() {
    this.tierCards.forEach((tierCard) => {
      if (tierCard === this.activeTierCard) return;

      this.syncSelectsToReference(tierCard.querySelectorAll('.ab-tiered-bundle-picker__variant-select'));
    });
  }

  syncSelectsToReference(selects) {
    if (!this.referenceVariantId) return;

    selects.forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;
      if (!select.querySelector(`option[value="${CSS.escape(this.referenceVariantId)}"]`)) return;

      select.value = this.referenceVariantId;
    });
  }

  getReferenceSelect() {
    const referenceSelect = Array.from(this.variantSelects).find((select) => select.value === this.referenceVariantId);
    return referenceSelect || this.activeVariantSelects[0] || this.variantSelects[0] || null;
  }

  updateProductPresentationFromReference() {
    this.updateProductPresentationFromSelect(this.getReferenceSelect());
  }

  async updateProductPresentationFromSelect(select) {
    if (!(select instanceof HTMLSelectElement) || !select.value) return;
    if (this.dataset.lastPresentedVariantId === select.value) return;

    this.dataset.lastPresentedVariantId = select.value;

    const requestUrl = new URL(window.location.href);
    requestUrl.searchParams.set('variant', select.value);

    this.presentationAbortController?.abort();
    this.presentationAbortController = new AbortController();

    try {
      const response = await fetch(requestUrl.toString(), {
        signal: this.presentationAbortController.signal,
      });
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const selectedVariantMediaId = this.selectedVariantMediaIdFromHtml(html);

      this.replaceMediaGallery(html, selectedVariantMediaId);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    }
  }

  selectedVariantMediaIdFromHtml(html) {
    const variantJson = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
    if (!variantJson) return null;

    try {
      return JSON.parse(variantJson)?.featured_media?.id?.toString() || null;
    } catch (error) {
      console.warn('Unable to read selected variant media id', error);
      return null;
    }
  }

  replaceMediaGallery(html, selectedVariantMediaId = null) {
    const currentGallery = document.querySelector('media-gallery');
    const nextGallery = html.querySelector('media-gallery');

    if (!currentGallery || !nextGallery) return;

    currentGallery.replaceWith(nextGallery);

    if (selectedVariantMediaId) {
      requestAnimationFrame(() => {
        if (typeof nextGallery.revealMediaById === 'function') {
          nextGallery.revealMediaById(selectedVariantMediaId, { onlyIfNeeded: true });
        }
      });
    }
  }

  updatePrices() {
    const prices = this.getSelectedPrices();
    const activeDiscountPct = this.getTierDiscountPct(this.activeTierCard);
    const activeDiscountedTotal = this.getDiscountedTotal(prices, activeDiscountPct);

    this.querySelectorAll('[data-tier-price]').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      const tierCard = element.closest('[data-tier-card]');
      const tierSelects = Array.from(tierCard?.querySelectorAll('.ab-tiered-bundle-picker__variant-select') || []);
      const tierPrices = tierSelects.map((select) => this.getSelectPrice(select));
      const tierCurrentTotal = tierPrices.reduce((sum, price) => sum + price, 0);
      const tierCompareTotal = tierSelects.reduce((sum, select) => sum + this.getSelectCompareAtPrice(select), 0);
      const discountPct = this.getTierDiscountPct(tierCard);
      const discountedTotal = this.getDiscountedTotal(tierPrices, discountPct);
      const automaticSavings = Math.max(0, tierCurrentTotal - discountedTotal);
      const compareTotal = discountPct > 0 ? tierCurrentTotal : tierCompareTotal;
      const hasCompareAtPrice =
        discountPct > 0
          ? tierCurrentTotal > discountedTotal
          : this.shouldShowCompareAtPrice(tierSelects, tierCurrentTotal, tierCompareTotal);
      const compareElement = element.querySelector('[data-tier-compare-price]');
      const currentElement = element.querySelector('[data-tier-current-price]');
      const savingsElement = element.querySelector('[data-tier-savings]');

      if (compareElement instanceof HTMLElement) {
        compareElement.textContent = hasCompareAtPrice ? this.formatPrice(compareTotal) : '';
        compareElement.hidden = !hasCompareAtPrice;
      }

      if (currentElement instanceof HTMLElement) {
        currentElement.textContent = this.formatPrice(discountedTotal);
      }

      if (savingsElement instanceof HTMLElement) {
        savingsElement.textContent = automaticSavings > 0 ? `Save ${this.formatPrice(automaticSavings)}` : '';
        savingsElement.hidden = automaticSavings <= 0;
      }
    });

    this.dataset.currentTotal = this.formatPrice(activeDiscountedTotal);
    this.updateStickyBar();
  }

  updateButtonLabel() {
    if (!this.button) return;

    const defaultLabel = this.button.dataset.defaultLabel || 'Add to cart';
    this.button.dataset.defaultComputedLabel = defaultLabel;
    this.setButtonState('default');
  }

  setupStickyObserver() {
    if (!this.stickyBar || !this.button || !('IntersectionObserver' in window)) return;

    this.stickyIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      const rect = entry.target.getBoundingClientRect();
      this.stickyTriggerPassed = !entry.isIntersecting && rect.bottom < 0;
      this.updateStickyVisibilityFromState();
    });

    this.stickyIntersectionObserver.observe(this.button);
  }

  setupStickyExitObserver() {
    if (!this.stickyBar || !('IntersectionObserver' in window)) return;

    const exitTargets = this.getStickyExitTargets();
    if (!exitTargets.length) return;

    this.stickyExitObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.stickyExitTargets.add(entry.target);
          } else {
            this.stickyExitTargets.delete(entry.target);
          }
        });

        this.stickyHiddenByExitTarget = this.stickyExitTargets.size > 0;
        this.updateStickyVisibilityFromState();
      },
      {
        rootMargin: '0px 0px 160px 0px',
      }
    );

    exitTargets.forEach((target) => this.stickyExitObserver.observe(target));
  }

  setupStickyFallback() {
    if (!this.stickyBar || !this.button) return;

    this.updateStickyTriggerFromButtonPosition();
    this.updateStickyVisibilityFromState();
    window.setTimeout(this.handleStickyFallbackRecalculate, 250);
    window.setTimeout(this.handleStickyFallbackRecalculate, 1000);
    window.addEventListener('scroll', this.handleStickyFallbackRecalculate, { passive: true });
    window.addEventListener('resize', this.handleStickyFallbackRecalculate);
  }

  handleStickyFallbackRecalculate = () => {
    if (this.stickyFallbackRaf) return;

    this.stickyFallbackRaf = requestAnimationFrame(() => {
      this.stickyFallbackRaf = null;
      this.updateStickyTriggerFromButtonPosition();
      this.updateStickyVisibilityFromState();
    });
  };

  updateStickyTriggerFromButtonPosition() {
    if (!this.button) return;

    const rect = this.button.getBoundingClientRect();
    this.stickyTriggerPassed = rect.bottom < 0;
  }

  getStickyExitTargets() {
    const selectors = ['product-recommendations', '.product-recommendations-wrapper', 'footer', '[class*="footer-group"]'];
    const targets = Array.from(document.querySelectorAll(selectors.join(','))).filter(
      (target) => target instanceof Element && !this.contains(target)
    );

    return Array.from(new Set(targets));
  }

  updateStickyVisibilityFromState() {
    this.setStickyVisible(this.stickyTriggerPassed && !this.stickyHiddenByExitTarget);
  }

  setupPreviewBarOffset() {
    if (!this.stickyBar) return;

    this.updatePreviewBarOffset();
    window.setTimeout(() => this.updatePreviewBarOffset(), 500);

    if (!('MutationObserver' in window) || !document.body) return;

    this.previewBarObserver = new MutationObserver(() => this.updatePreviewBarOffset());
    this.previewBarObserver.observe(document.body, { childList: true });
  }

  updatePreviewBarOffset() {
    if (!this.stickyBar) return;

    const previewBar = document.querySelector('#PBarNextFrameWrapper');
    if (previewBar instanceof HTMLElement) {
      const previewBarHeight = Math.ceil(previewBar.getBoundingClientRect().height || 68);
      this.stickyBar.style.setProperty(
        '--ab-bundle-sticky-bottom',
        `calc(${previewBarHeight + 16}px + env(safe-area-inset-bottom))`
      );
      return;
    }

    this.stickyBar.style.removeProperty('--ab-bundle-sticky-bottom');
  }

  setStickyVisible(visible) {
    if (!this.stickyBar) return;

    if (visible) {
      this.stickyBar.dataset.visible = 'true';
    } else {
      this.stickyBar.dataset.visible = 'false';
    }
  }

  updateStickyBar() {
    if (!this.stickyBar) return;

    const selectedLabels = this.getSelectedVariantLabels();
    const hasSelection = selectedLabels.length > 0;

    if (this.stickySummary) {
      this.renderStickySummary(this.getStickySummaryParts());
    }

    if (this.stickyVariants) {
      this.stickyVariants.textContent = hasSelection ? selectedLabels.join(' + ') : 'Choose your styles';
    }

    if (this.stickyButton) {
      this.stickyButton.disabled = !hasSelection;
    }
  }

  getStickySummaryParts() {
    const tierLabel = this.activeTierCard?.querySelector('.ab-tiered-bundle-picker__title')?.textContent?.trim();
    const discountPct = this.getTierDiscountPct(this.activeTierCard);
    const savingsText = this.activeTierCard
      ?.querySelector('[data-tier-savings]')
      ?.textContent?.replace(/\s+/g, ' ')
      .trim();
    const total = this.dataset.currentTotal;

    return [
      { kind: 'title', text: tierLabel },
      { kind: 'discount', text: discountPct > 0 ? `${discountPct}% off` : '' },
      { kind: 'savings', text: savingsText },
      { kind: 'total', text: total },
    ].filter((part) => part.text);
  }

  renderStickySummary(parts) {
    const stickySummary = this.stickySummary;
    if (!stickySummary) return;

    const fragment = document.createDocumentFragment();
    parts.forEach((part, index) => {
      if (index > 0) {
        const separator = document.createElement('span');
        separator.className = 'ab-tiered-bundle-picker__sticky-separator';
        separator.textContent = '·';
        fragment.append(separator);
      }

      const partElement = document.createElement('span');
      partElement.className = 'ab-tiered-bundle-picker__sticky-part';
      partElement.dataset.summaryPart = part.kind;
      partElement.textContent = part.text;
      fragment.append(partElement);
    });

    stickySummary.replaceChildren(fragment);
  }

  getActiveTierAnalyticsPayload() {
    return {
      bundle_type: 'tiered',
      product_id: this.dataset.productId,
      tier_count: this.activeTierCount,
      tier_label: this.activeTierCard?.querySelector('.ab-tiered-bundle-picker__title')?.textContent?.trim(),
      discount_pct: this.getTierDiscountPct(this.activeTierCard),
      selected_variant_ids: this.getSelectedVariantIds(),
      selected_variant_count: this.getSelectedVariantIds().length,
      total_text: this.dataset.currentTotal,
    };
  }

  getSelectedVariantIds() {
    return Array.from(this.activeVariantSelects)
      .map((select) => select.value)
      .filter(Boolean);
  }

  getSelectedVariantLabels() {
    return Array.from(this.activeVariantSelects)
      .map((select) => select.selectedOptions[0]?.textContent?.replace(/\s+/g, ' ').trim())
      .filter(Boolean);
  }

  getSelectedPrices() {
    return Array.from(this.activeVariantSelects)
      .map((select) => this.getSelectPrice(select))
      .filter((price) => Number.isFinite(price));
  }

  getSelectPrice(select) {
    if (!(select instanceof HTMLSelectElement)) return 0;

    const option = select.selectedOptions[0];
    return Number(option?.dataset.price || 0);
  }

  getSelectCompareAtPrice(select) {
    if (!(select instanceof HTMLSelectElement)) return 0;

    const option = select.selectedOptions[0];
    return Number(option?.dataset.compareAtPrice || 0);
  }

  shouldShowCompareAtPrice(selects, currentTotal, compareTotal) {
    if (!this.showCompareAtPrice || !selects.length || compareTotal <= currentTotal) return false;

    return selects.every((select) => {
      const price = this.getSelectPrice(select);
      const compareAtPrice = this.getSelectCompareAtPrice(select);
      return compareAtPrice > price;
    });
  }

  getTierDiscountPct(tierCard) {
    const discountPct = Number(tierCard?.getAttribute(TIER_DISCOUNT_ATTRIBUTE) || 0);
    if (!Number.isFinite(discountPct)) return 0;
    return Math.min(100, Math.max(0, discountPct));
  }

  getDiscountedTotal(prices, discountPct) {
    const total = prices.reduce((sum, price) => sum + price, 0);
    if (!discountPct) return total;
    return prices.reduce((sum, price) => {
      const discountAmount = Math.floor(price * (discountPct / 100));
      return sum + Math.max(0, price - discountAmount);
    }, 0);
  }

  buildCartItems(variantIds) {
    const quantityByVariant = new Map();

    variantIds.forEach((variantId) => {
      quantityByVariant.set(variantId, (quantityByVariant.get(variantId) || 0) + 1);
    });

    return Array.from(quantityByVariant.entries()).map(([variantId, quantity]) => ({
      id: Number(variantId),
      quantity,
    }));
  }

  getCartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : null))
      .filter(Boolean);
  }

  formatPrice(price) {
    return formatMoney(price, this.moneyFormat, this.currency);
  }

  applyPurchaseControlVisibility() {
    if (this.dataset.hideNativePurchaseControls !== 'true') return;

    const productDetails = this.closest('.product-details');
    const buyButtons = productDetails?.querySelector('.buy-buttons-block');
    const variantPicker = productDetails?.querySelector('.variant-picker');
    const stickyAddToCart = document.querySelector('sticky-add-to-cart');

    if (variantPicker instanceof HTMLElement) {
      variantPicker.hidden = true;
      variantPicker.dataset.abHiddenByTieredBundlePicker = 'true';
    }

    if (buyButtons instanceof HTMLElement) {
      buyButtons.hidden = true;
      buyButtons.dataset.abHiddenByTieredBundlePicker = 'true';
    }

    if (stickyAddToCart instanceof HTMLElement) {
      stickyAddToCart.hidden = true;
      stickyAddToCart.dataset.abHiddenByTieredBundlePicker = 'true';
    }
  }

  restorePurchaseControlVisibility() {
    const productDetails = this.closest('.product-details');
    const variantPicker = productDetails?.querySelector('[data-ab-hidden-by-tiered-bundle-picker="true"].variant-picker');
    const buyButtons = productDetails?.querySelector(
      '.buy-buttons-block[data-ab-hidden-by-tiered-bundle-picker="true"]'
    );
    const stickyAddToCart = document.querySelector('sticky-add-to-cart[data-ab-hidden-by-tiered-bundle-picker="true"]');

    if (variantPicker instanceof HTMLElement) {
      variantPicker.hidden = false;
      delete variantPicker.dataset.abHiddenByTieredBundlePicker;
    }

    if (buyButtons instanceof HTMLElement) {
      buyButtons.hidden = false;
      delete buyButtons.dataset.abHiddenByTieredBundlePicker;
    }

    if (stickyAddToCart instanceof HTMLElement) {
      stickyAddToCart.hidden = false;
      delete stickyAddToCart.dataset.abHiddenByTieredBundlePicker;
    }
  }

  handleCartError(payload) {
    const message = payload?.message || payload?.description || 'We could not add this offer. Please try again.';
    this.showError(message);

    document.dispatchEvent(
      new CartErrorEvent(this.id || 'ab-tiered-bundle-picker', message, payload?.description, payload?.errors)
    );

    this.setButtonState('default');
  }

  showError(message) {
    if (!this.errorElement) return;

    this.errorElement.textContent = message;
    this.errorElement.hidden = false;
  }

  clearError() {
    if (!this.errorElement) return;

    this.errorElement.textContent = '';
    this.errorElement.hidden = true;
  }

  setButtonState(state) {
    this.setPurchaseButtonState(this.button, state);
    this.setPurchaseButtonState(this.stickyButton, state);
  }

  setPurchaseButtonState(button, state) {
    if (!button) return;

    const labels = {
      default: button.dataset.defaultComputedLabel || button.dataset.defaultLabel || 'Add to cart',
      adding: button.dataset.addingLabel || button.dataset.defaultComputedLabel || button.dataset.defaultLabel || 'Add to cart',
      added: button.dataset.addedLabel || button.dataset.defaultComputedLabel || button.dataset.defaultLabel || 'Add to cart',
    };

    const content = button.querySelector('.add-to-cart-text__content span span');
    if (content) {
      content.textContent = labels[state] || labels.default;
    } else {
      button.textContent = labels[state] || labels.default;
    }

    button.disabled = state === 'adding';
    button.toggleAttribute('aria-busy', state === 'adding');
  }

  animateAddToCart() {
    if (!this.button) return;

    this.button.dataset.added = 'true';
    if (this.stickyButton) {
      this.stickyButton.dataset.added = 'true';
    }
    window.setTimeout(() => {
      if (this.button) {
        delete this.button.dataset.added;
      }
      if (this.stickyButton) {
        delete this.stickyButton.dataset.added;
      }
    }, 900);
  }

  animateFlyToCart() {
    if (!this.button || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const selectedImage = this.getReferenceSelect()?.selectedOptions[0]?.dataset.imageSrc;
    if (!cartIcon || !selectedImage || !customElements.get('fly-to-cart')) return;

    const flyToCartElement = document.createElement('fly-to-cart');
    flyToCartElement.classList.add('fly-to-cart--main');
    flyToCartElement.style.setProperty('background-image', `url(${selectedImage})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = this.button;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);
  }
}

if (!customElements.get('ab-tiered-bundle-picker')) {
  customElements.define('ab-tiered-bundle-picker', ABTieredBundlePicker);
}
